import moment from 'moment';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import React from 'react';

import { DISPLAY_DATE_FORMAT_LONG_WITH_DAY } from '../../../config/helpers';
import formatPrice from '../../../util/formatPrice';

import Icon from '../Atoms/Icon';
import Picture from '../Picture';
import { currencyCodeAndSymbolPropTypes } from '../../../util/currency';

const propTypes = exact({
  currentCurrency: currencyCodeAndSymbolPropTypes,
  date: PropTypes.string.isRequired,
  description: PropTypes.string,
  fromPriceText: PropTypes.string,
  image: PropTypes.object,
  inclusions: PropTypes.arrayOf(PropTypes.string),
  isSoldOut: PropTypes.bool,
  perPersonText: PropTypes.string,
  price: PropTypes.number.isRequired,
  status: PropTypes.string,
  soldOutText: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  tournamentName: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  limitedAvailability: PropTypes.bool.isRequired,
  limitedAvailabilityRemainingSpaces: PropTypes.string.isRequired,
  limitedAvailabilityText: PropTypes.string.isRequired,
  limitedAvailabilityTextOverride: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  viewTourText: PropTypes.string,
});

const defaultProps = {
  description: null,
  fromPriceText: 'From',
  image: null,
  inclusions: [],
  isSoldOut: false,
  perPersonText: 'pp',
  status: null,
  viewTourText: 'view package',
};

function FeaturedPackage(props) {
  const limitedAvailabilityText =
    props.limitedAvailabilityTextOverride ||
    props.limitedAvailabilityText ||
    'Limited Availability';

  return (
    <article className={`FeaturedItem ${props.status}`}>
      {props.limitedAvailability && (
        <div className="FeaturedItem_limitedAvailability">
          {limitedAvailabilityText
            ? limitedAvailabilityText.replace(
                '{LimitedAvailabilityRemainingSpaces}',
                props.limitedAvailabilityRemainingSpaces
              )
            : null}
        </div>
      )}
      {props.image && (
        <div className="FeaturedItem_tint">
          <Picture
            {...props.image}
            sizes={[
              [100, '640'], // from 0
              [100, '640'], // from 480
              [100, '640'], // from 768
              [100, '640'], // from 1024
              [100, '640'], // from 1440
            ]}
            className="FeaturedItem_figure"
            loading={props.index === 0 ? 'eager' : 'lazy'}
          />
        </div>
      )}
      <div className="FeaturedItem_container">
        <div className="FeaturedItem_content">
          <h3 className="FeaturedItem_tournament FeaturedItem_animation">{props.tournamentName}</h3>
          <h2 className="FeaturedItem_title FeaturedItem_animation">{props.title}</h2>
          {props.description && (
            <div className="FeaturedItem_description FeaturedItem_animation">
              {props.description}
            </div>
          )}
          <time dateTime={props.date} className="FeaturedItem_date FeaturedItem_animation">
            {moment(props.date).format(DISPLAY_DATE_FORMAT_LONG_WITH_DAY)}
          </time>
          <div className="FeaturedItem_fromPrice FeaturedItem_animation">
            {props.fromPriceText} {props.currentCurrency.symbol}
            {formatPrice(props.price)}
            {props.perPersonText}
          </div>
          {props.inclusions.length > 0 && (
            <div className="FeaturedItem_inclusions FeaturedItem_animation">
              <ul className="FeaturedItem_inclusionsList">
                {props.inclusions.map((inclusion) => (
                  <li key={inclusion} className="FeaturedItem_inclusionsItem">
                    <Icon id={inclusion} key={inclusion} blockName="FeaturedItem_inclusionsIcon" />
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="FeaturedItem_cta FeaturedItem_animation">
            {props.isSoldOut && <span className="FeaturedItem_soldOut">{props.soldOutText}</span>}
            {!props.isSoldOut && (
              <a href={props.url} className="FeaturedItem_button Button Button-ghost">
                <span className="Button_text">{props.viewTourText}</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

FeaturedPackage.propTypes = propTypes;
FeaturedPackage.defaultProps = defaultProps;

export default FeaturedPackage;
